// AppRouter.tsx

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

// --- Finance chunk ---
const Finance = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/Finance'));
const EarningsIncentives = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/EarningsIncentives'));
const TransactionHistory = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/TransactionHistory'));
const CodSettlement = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/CodSettlement'));
const CodSettlementHistory = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/CodSettlementHistory'));
const PendingCodSettlements = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/PendingCodSettlements'));
const CashLimitDetails = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/CashLimitDetails'));
const Invoices = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/Invoices'));
const ErrorPage = React.lazy(() => import(/* webpackChunkName: "finance" */ './components/finance/ErrorPage'));

// --- Profile chunk ---
const Profile = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/Profile'));
const DrivingLicense = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/DrivingLicense'));
const ChangePassword = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/ChangePassword'));
const CompleteProfile = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/CompleteProfile'));
const SelectEmergencyContact = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/SelectEmergencyContact'));
const ChangeEmail = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/ChangeEmail'));
const ChangePhone = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/ChangePhone'));
const ProfileV2 = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/ProfileV2'));
const ProfileV2Edit = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/ProfileV2Edit'));
const SelectNomineeContact = React.lazy(() => import(/* webpackChunkName: "profile" */ './components/profile/SelectNomineeContact'));

// --- Help & Support (eager, pre-login deep link entry) ---
import HelpAndSupport from './components/help-and-support/HelpAndSupport';

// --- SSO Callback (eager, pre-login — Android JS bridge handoff) ---
import SsoCallback from './components/sso-callback/SsoCallback';

// --- Exchange chunk ---
const Assessment = React.lazy(() => import(/* webpackChunkName: "exchange" */ './components/exchange/assessment/assessment-components/Assessment'));
const Consent = React.lazy(() => import(/* webpackChunkName: "exchange" */ './components/exchange/assessment/assessment-components/consent/Consent'));
const DetachSummary = React.lazy(() => import(/* webpackChunkName: "exchange" */ './components/exchange/assessment/assessment-components/detach/DetachSummary'));

// --- Referrals chunk ---
const Referrals = React.lazy(() => import(/* webpackChunkName: "referrals" */ './components/referrals/Referrals'));
const MyReferrals = React.lazy(() => import(/* webpackChunkName: "referrals" */ './components/referrals/MyReferrals'));
const ReferralDetails = React.lazy(() => import(/* webpackChunkName: "referrals" */ './components/referrals/ReferralDetails'));
const ReferNowPage = React.lazy(() => import(/* webpackChunkName: "referrals" */ './components/referrals/ReferNowPage'));

// --- Worker Payout chunk ---
const WorkerPayout = React.lazy(() => import(/* webpackChunkName: "workerpayout" */ './components/workerpayout/WorkerPayout'));
const Incentives = React.lazy(() => import(/* webpackChunkName: "workerpayout" */ './components/workerpayout/Incentives'));
const MinimumGuarantee = React.lazy(() => import(/* webpackChunkName: "workerpayout" */ './components/workerpayout/MinimumGuarantee'));
const RateCard = React.lazy(() => import(/* webpackChunkName: "workerpayout" */ './components/workerpayout/RateCard'));
const OrderBreakdown = React.lazy(() => import(/* webpackChunkName: "workerpayout" */ './components/workerpayout/OrderBreakdown'));
const AdhocSurgeBanner = React.lazy(() => import(/* webpackChunkName: "adhoc-surge-banner" */ './components/adhoc-surge-banner'));

// --- Parcel Connect chunk ---
const ParcelConnect = React.lazy(() => import(/* webpackChunkName: "parcel-connect" */ './components/parcel-connect/ParcelConnect'));
const PickupScanning = React.lazy(() => import(/* webpackChunkName: "parcel-connect" */ './components/parcel-connect/PickupScanning'));
const DeliveryScanning = React.lazy(() => import(/* webpackChunkName: "parcel-connect" */ './components/parcel-connect/DeliveryScanning'));

// --- Trip Audit chunk ---
const TripAudit = React.lazy(() => import(/* webpackChunkName: "trip-audit" */ './components/trip-audit/TripAudit'));
const ScanParcels = React.lazy(() => import(/* webpackChunkName: "trip-audit" */ './components/trip-audit/ScanParcels'));
const AuditSummary = React.lazy(() => import(/* webpackChunkName: "trip-audit" */ './components/trip-audit/AuditSummary'));

// --- Trip Summary chunk ---
const TripOverView = React.lazy(() => import(/* webpackChunkName: "trip-summary" */ './components/trip-summary/TripOverView'));
const ConsignmentList = React.lazy(() => import(/* webpackChunkName: "trip-summary" */ './components/trip-summary/ConsignmentList'));
const CollectionDetail = React.lazy(() => import(/* webpackChunkName: "trip-summary" */ './components/trip-summary/CollectionDetail'));

// --- Gamification chunk ---
const Gamification = React.lazy(() => import(/* webpackChunkName: "gamification" */ './components/gamification/Gamification'));
const MetricDetailPage = React.lazy(() => import(/* webpackChunkName: "gamification" */ './components/gamification/MetricDetailPage'));

// --- Shift chunk ---
const Shift = React.lazy(() => import(/* webpackChunkName: "shift" */ './components/shift/Shift'));
const NewShift = React.lazy(() => import(/* webpackChunkName: "shift" */ './components/shift/NewShift'));
const ShiftPreference = React.lazy(() => import(/* webpackChunkName: "shift" */ './components/shift/ShiftPreference'));

// --- Selfie Verification chunk ---
const SelfieVerification = React.lazy(() => import(/* webpackChunkName: "selfie-verification" */ './components/selfie-verification/SelfieVerification'));

// --- Aadhaar Verification chunk ---
const AadhaarVerification = React.lazy(() => import(/* webpackChunkName: "aadhaar-verification" */ './components/aadhaar-verification/AadhaarVerification'));

// --- DigiLocker Verification chunk --- ISS-289464
const DigilockerVerification = React.lazy(() => import(/* webpackChunkName: "digilocker-verification" */ './components/digilocker-verification/DigilockerVerification'));

// --- Hub Selection chunk ---
const HubSelection = React.lazy(() => import(/* webpackChunkName: "hub-selection" */ './components/hub-selection/HubSelection'));

// --- Rider Support chunk — ISS-285392 ---
const RiderSupport = React.lazy(() => import(/* webpackChunkName: "rider-support" */ './components/rider-support/RiderSupport'));

// --- Standalone chunks ---
const RazorpayPayment = React.lazy(() => import(/* webpackChunkName: "razorpay" */ './components/razorpay/RazorpayPayment'));
const RainMode = React.lazy(() => import(/* webpackChunkName: "rainmode" */ './components/rainmode/RainMode'));
const NotificationRoot = React.lazy(() => import(/* webpackChunkName: "notification" */ './components/notification'));
const TaskReview = React.lazy(() => import(/* webpackChunkName: "task-review" */ './components/task-review/TaskReview'));

// --- Returnable Assets Reconciliation Self-Serve — ISS-288035 ---
const ReturnableAssetsRecon = React.lazy(() => import(/* webpackChunkName: "returnable-assets-recon" */ './components/returnable-assets-recon/ReturnableAssetsRecon'));

// --- Equipment Wallet (Asset Wallet) chunk — ISS-285582 ---
const EquipmentWallet = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/EquipmentWallet'));
const ConfirmAssets = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/ConfirmAssets/ConfirmAssets'));
const VerifyAssets = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/ConfirmAssets/VerifyAssets'));
const OtpScreen = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/OtpScreen/OtpScreen'));
const Eligibility = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/Eligibility/Eligibility'));
const Transactions = React.lazy(() => import(/* webpackChunkName: "equipment-wallet" */ './components/equipment-wallet/Transactions/Transactions'));

// --- Joining Bonus chunk — ISS-292770 ---
const JoiningBonus = React.lazy(() => import(/* webpackChunkName: "joining-bonus" */ './components/joining-bonus/JoiningBonus'));

const BroadcastTrips = React.lazy(() => import(/* webpackChunkName: "broadcast-trips" */ './components/broadcast-trips/BroadcastTrips'));

const JobInvoices = React.lazy(() => import(/* webpackChunkName: "job-invoices" */ './components/job_invoices/JobInvoices'));
const InvoiceDetail = React.lazy(() => import(/* webpackChunkName: "job-invoices" */ './components/job_invoices/InvoiceDetail'));

const AppRouter: React.FC = () => (
    <Router>
      <Suspense fallback={null}>
        <Switch>
          <Route exact path="/exchange" component={Assessment} />
          <Route exact path="/exchange/assessment" component={Assessment} />
          <Route
            exact
            path="/exchange/assessment/:assessmentId/:trackingId/:questionId/:questionAttribute"
            component={Assessment}
          />

          <Route exact path="/error" render={(props) => (
            <ErrorPage
              {...props}
              title="An error occurred"
              description="Please try again or contact support."
            />
          )} />
          <Route exact path="/exchange/assessment/consent" component={Consent} />
          <Route exact path="/exchange/assessment/consent/detach" component={DetachSummary} />

          <Route exact path="/finance" component={Finance} />
          <Route exact path="/finance/earnings" component={EarningsIncentives} />
          <Route exact path="/finance/transactions" component={TransactionHistory} />
          <Route exact path="/finance/cod-settlement" component={CodSettlement} />
          <Route exact path="/finance/cod-settlement-history" component={CodSettlementHistory} />
          <Route exact path="/finance/pending-cod-settlements" component={PendingCodSettlements} />
          <Route exact path="/finance/cash-limit-details" component={CashLimitDetails} />
          <Route exact path="/profile" component={Profile} />
          <Route exact path="/profile/v2" component={ProfileV2} />
          <Route exact path="/profile/v2/edit" component={ProfileV2Edit} />
          <Route exact path="/profile/driving-license" component={DrivingLicense} />
          <Route exact path="/finance/invoices" component={Invoices} />
          <Route exact path="/profile/change-password" component={ChangePassword} />
          <Route exact path="/profile/change-email" component={ChangeEmail} />
          <Route exact path="/profile/change-phone" component={ChangePhone} />
          <Route exact path="/profile/complete-profile" component={CompleteProfile} />
          <Route exact path="/profile/select-emergency-contact" component={SelectEmergencyContact} />
          <Route exact path="/profile/select-nominee-contact" component={SelectNomineeContact} />
          <Route exact path="/payment/rzp" component={RazorpayPayment} />

          <Route exact path="/task_review" component={TaskReview} />
          <Route exact path="/referrals" component={Referrals} />
          <Route exact path="/referrals/refer-now" component={ReferNowPage} />
          <Route exact path="/referrals/list" component={MyReferrals} />
          <Route exact path="/referrals/fetchOne" component={ReferralDetails} />

          <Route exact path='/workerpayout' component={WorkerPayout} />
          <Route exact path='/workerpayout/incentives' component={Incentives} />
          <Route exact path='/workerpayout/minimumguarantee' component={MinimumGuarantee} />
          <Route exact path='/workerpayout/ratecard' component={RateCard} />
          <Route exact path='/workerpayout/order' component={OrderBreakdown}/>
          <Route exact path='/workerpayout/adhocsurgebanner' component={AdhocSurgeBanner} />

          <Route exact path="/trip_summary" component={TripOverView} />
          <Route exact path="/trip_summary/consignment_list" component={ConsignmentList} />
          <Route exact path="/trip_summary/collection_detail" component={CollectionDetail} />

          <Route exact path="/rainmode" component={RainMode} />
          <Route exact path="/shift_new" component={NewShift} />
          <Route exact path="/shift_preference" component={ShiftPreference} />
          <Route exact path="/shift" component={Shift} />
          <Route exact path="/parcel_connect" component={ParcelConnect} />
          <Route exact path="/parcel_connect/pickup-scanning/:hubAgentCode/:hubId" component={PickupScanning} />
          <Route exact path="/parcel_connect/delivery-scanning/:hubAgentCode/:hubId" component={DeliveryScanning} />
          <Route exact path="/gamification" component={Gamification} />
          <Route exact path="/gamification/metric/:ruleId" component={MetricDetailPage} />
          
          <Route exact path="/trip_audit" component={TripAudit} />
          <Route exact path="/trip_audit/scan_parcels" component={ScanParcels} />
          <Route exact path="/trip_audit/audit_summary" component={AuditSummary} />
          <Route path="/notification" component={NotificationRoot} />
          <Route exact path="/broadcast_trips" component={BroadcastTrips} />
          <Route exact path="/job_invoices" component={JobInvoices} />
          <Route exact path="/job_invoices/detail" component={InvoiceDetail} />

          {/* Equipment Wallet — ISS-285582. Path uses underscore to match the
              drawer-item id `equipment_wallet` returned by webview/url so no
              slug rewrite is needed. */}
          <Route exact path="/equipment_wallet" component={EquipmentWallet} />
          <Route exact path="/equipment_wallet/confirm/:sessionId" component={ConfirmAssets} />
          <Route exact path="/equipment_wallet/verify/:sessionId" component={VerifyAssets} />
          <Route exact path="/equipment_wallet/otp/:sessionId" component={OtpScreen} />
          <Route exact path="/equipment_wallet/eligibility" component={Eligibility} />
          <Route exact path="/equipment_wallet/transactions" component={Transactions} />

          {/* Joining Bonus — ISS-292770. Path == drawer module id `joining_bonus`
              (mobile-web-view.js supportedModules), same convention as above. */}
          <Route exact path="/joining_bonus" component={JoiningBonus} />

          <Route exact path="/returnable_assets_recon" component={ReturnableAssetsRecon} />
          <Route exact path="/selfie-verification" component={SelfieVerification} />
          <Route exact path="/aadhaar-verification" component={AadhaarVerification} />
          <Route exact path="/digilocker-verification" component={DigilockerVerification} />
          <Route exact path="/help-support" component={HelpAndSupport} />
          <Route exact path="/sso-callback" component={SsoCallback} />
          <Route exact path="/hub_selection" component={HubSelection} />
          <Route exact path="/rider-support" component={RiderSupport} />
          <Route component={Finance} />
        </Switch>
      </Suspense>
    </Router>
);

export default AppRouter;
